import { useState } from 'react';
import { Heading } from './Heading';
import { spacingClass } from '@/utils/spacing';
import { useI18n } from '@/components/_vs/i18n/I18nProvider';

/**
 * Iscrizione newsletter (Brevo) — inline, senza redirect. Il consenso è un
 * checkbox dedicato e obbligatorio, separato dal banner cookie: iscriversi
 * alla newsletter è una raccolta dati con una propria base giuridica, non un
 * cookie (vedi ConsentProvider per il consenso cookie).
 * @param {{ title?: string, subtitle?: string, firstNamePlaceholder?: string, lastNamePlaceholder?: string, placeholder?: string, buttonLabel?: string, consentLabel?: string, successMessage?: string, brevoListId?: string, brevoTag?: string, notifyEmails?: string[], spacing?: string, headingLevel?: number }} props
 */
export function NewsletterBlock({
  title, subtitle,
  firstNamePlaceholder = 'Nome',
  lastNamePlaceholder = 'Cognome',
  placeholder = 'La tua email',
  buttonLabel = 'Iscriviti',
  consentLabel = "Acconsento al trattamento dei dati per ricevere la newsletter.",
  successMessage = 'Iscrizione confermata — grazie!',
  brevoListId = '',
  brevoTag = '',
  notifyEmails = [],
  spacing = 'normal',
  headingLevel = 2,
}) {
  const { locale } = useI18n();
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [consent, setConsent] = useState(false);
  const [website, setWebsite] = useState(''); // honeypot: campo invisibile, i bot lo compilano
  const [status, setStatus] = useState('idle'); // idle | loading | success | error
  const [error, setError] = useState('');

  async function handleSubmit(e) {
    e.preventDefault();
    if (status === 'loading') return;
    setStatus('loading');
    setError('');
    try {
      const res = await fetch('/api/newsletter/subscribe', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          firstName, lastName, email, consent, locale, honeypot: website,
          listId: brevoListId || undefined,
          tag: brevoTag || undefined,
          notifyEmails: (notifyEmails || []).filter(Boolean),
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || !data.ok) throw new Error(data.error || 'Errore durante l\'iscrizione.');
      setStatus('success');
    } catch (err) {
      setStatus('error');
      setError(err.message);
    }
  }

  return (
    <section className={'px-6 md:px-16 ' + spacingClass(spacing)}>
      <div className="max-w-xl mx-auto text-center">
        {title && <Heading level={headingLevel} className="font-heading text-h1 font-light text-theme-text mb-4">{title}</Heading>}
        {subtitle && <p className="text-body-lg text-theme-text-muted leading-relaxed mb-9">{subtitle}</p>}

        {status === 'success' ? (
          <p role="status" className="text-sm text-theme-accent font-medium">{successMessage}</p>
        ) : (
          <form onSubmit={handleSubmit} className="flex flex-col items-center gap-4 max-w-2xl mx-auto">
            <input
              type="text"
              name="website"
              value={website}
              onChange={(e) => setWebsite(e.target.value)}
              tabIndex={-1}
              autoComplete="off"
              className="hidden"
              aria-hidden="true"
            />
            <div className="flex flex-col sm:flex-row items-stretch gap-2.5 w-full">
              <input
                type="text"
                required
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
                placeholder={firstNamePlaceholder}
                className="flex-1 basis-0 min-w-0 rounded-lg border border-theme-border bg-theme-bg-surface px-4 py-3 text-sm text-theme-text outline-none focus:border-theme-accent transition-colors"
              />
              <input
                type="text"
                required
                value={lastName}
                onChange={(e) => setLastName(e.target.value)}
                placeholder={lastNamePlaceholder}
                className="flex-1 basis-0 min-w-0 rounded-lg border border-theme-border bg-theme-bg-surface px-4 py-3 text-sm text-theme-text outline-none focus:border-theme-accent transition-colors"
              />
              <input
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder={placeholder}
                className="flex-1 basis-0 min-w-0 rounded-lg border border-theme-border bg-theme-bg-surface px-4 py-3 text-sm text-theme-text outline-none focus:border-theme-accent transition-colors"
              />
            </div>
            <label className="flex items-start gap-2 text-center text-xs text-theme-text-muted cursor-pointer">
              <input
                type="checkbox"
                checked={consent}
                onChange={(e) => setConsent(e.target.checked)}
                required
                className="mt-0.5 w-4 h-4 rounded border-theme-border shrink-0"
              />
              <span>{consentLabel}</span>
            </label>
            <button
              type="submit"
              disabled={status === 'loading' || !consent}
              className="inline-flex items-center justify-center px-8 py-3 rounded-lg bg-theme-accent text-theme-accent-text text-sm font-medium tracking-wide disabled:opacity-50 transition-opacity"
            >
              {status === 'loading' ? '...' : buttonLabel}
            </button>
            {status === 'error' && <p role="alert" className="text-xs text-red-500">{error}</p>}
          </form>
        )}
      </div>
    </section>
  );
}
